"use client";

import { useEffect } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  panelClass?: string;
  variant?: "default" | "lightbox";
}

export default function Modal({
  open,
  onClose,
  children,
  panelClass = "",
  variant = "default",
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const overlayBg = variant === "lightbox" ? "bg-black/95" : "bg-ss-text/65";

  return (
    <div
      className={`fixed inset-x-0 top-0 h-[100dvh] z-[250] flex items-center justify-center p-6 ${overlayBg} backdrop-blur`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 end-4 w-10 h-10 rounded-full bg-ss-text/55 backdrop-blur text-white grid place-items-center hover:bg-ss-text z-10 text-xl"
      >
        ×
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className={
          variant === "lightbox"
            ? `bg-transparent w-full h-full max-h-[100dvh] ${panelClass}`
            : `bg-white rounded-[22px] w-full max-w-[480px] max-h-[calc(100dvh-48px)] overflow-y-auto p-8 shadow-[var(--shadow-ss-xl)] fade-up ${panelClass}`
        }
      >
        {children}
      </div>
    </div>
  );
}
